import React, { useState, useEffect } from 'react';
import { Plus, Briefcase, MapPin, Calendar, DollarSign, Users, Clock, X, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { collaborationsService } from '../services/collaborationsService';
import { ArtCategory } from '../types';
import CollaborationDetailModal from './CollaborationDetailModal';
import { ContentModerationError } from '../lib/contentModeration';

const statusColors = {
  open: 'bg-green-100 text-green-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
};

const applicationStatusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const CollaborationsView: React.FC = () => {
  const { user } = useAuth();
  const { select, t, locale } = useLanguage();
  const [activeTab, setActiveTab] = useState('browse');
  const [collaborations, setCollaborations] = useState<Record<string, unknown>[]>([]);
  const [myCollaborations, setMyCollaborations] = useState<Record<string, unknown>[]>([]);
  const [myApplications, setMyApplications] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCollaboration, setSelectedCollaboration] = useState<Record<string, unknown> | null>(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [createError, setCreateError] = useState('');
  const [applicationError, setApplicationError] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [filters, setFilters] = useState({
    category: 'all' as ArtCategory | 'all',
    status: 'all' as 'open' | 'in_progress' | 'completed' | 'cancelled' | 'all',
    location: '',
  });

  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    category: '' as ArtCategory,
    location: '',
    budget: '',
    deadline: '',
    requirements: '',
  });

  const categoryLabels = React.useMemo(
    () => ({
      music: t('categories.music', select({ es: 'Música', en: 'Music' })),
      writing: t('categories.writing', select({ es: 'Escritura', en: 'Writing' })),
      tattoo: t('categories.tattoo', select({ es: 'Tatuajes', en: 'Tattoos' })),
      digital_design: t('categories.digital_design', select({ es: 'Diseño Digital', en: 'Digital Design' })),
      fashion_design: t('categories.fashion_design', select({ es: 'Diseño de Moda', en: 'Fashion Design' })),
      photography: t('categories.photography', select({ es: 'Fotografía', en: 'Photography' })),
      sculpture: t('categories.sculpture', select({ es: 'Escultura', en: 'Sculpture' })),
      painting: t('categories.painting', select({ es: 'Pintura', en: 'Painting' })),
      dance: t('categories.dance', select({ es: 'Danza', en: 'Dance' })),
      filmmaking: t('categories.filmmaking', select({ es: 'Cine', en: 'Filmmaking' })),
      makeup: t('categories.makeup', select({ es: 'Maquillaje', en: 'Makeup' })),
    }),
    [select, t]
  );

  const statusLabels = React.useMemo(
    () => ({
      open: select({ es: 'Abierta', en: 'Open' }),
      in_progress: select({ es: 'En progreso', en: 'In progress' }),
      completed: select({ es: 'Completada', en: 'Completed' }),
      cancelled: select({ es: 'Cancelada', en: 'Cancelled' }),
    }),
    [select]
  );

  const applicationStatusLabels = React.useMemo(
    () => ({
      pending: select({ es: 'Pendiente', en: 'Pending' }),
      accepted: select({ es: 'Aceptada', en: 'Accepted' }),
      rejected: select({ es: 'Rechazada', en: 'Rejected' }),
    }),
    [select]
  );

  useEffect(() => {
    void loadCollaborations();
    if (user) {
      void loadMyCollaborations();
      void loadMyApplications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, user]);

  const loadCollaborations = async () => {
    try {
      setLoading(true);
      const data = await collaborationsService.getCollaborations({
        category: filters.category,
        status: filters.status,
        location: filters.location,
        limit: 20,
      });
      setCollaborations(data);
    } catch (err) {
      console.error('Error loading collaborations:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMyCollaborations = async () => {
    try {
      const data = await collaborationsService.getMyCollaborations();
      setMyCollaborations(data);
    } catch (err) {
      console.error('Error loading my collaborations:', err);
    }
  };

  const loadMyApplications = async () => {
    try {
      const data = await collaborationsService.getMyApplications();
      setMyApplications(data);
    } catch (err) {
      console.error('Error loading my applications:', err);
    }
  };

  const handleCreateCollaboration = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');
    try {
      const newCollaboration = await collaborationsService.createCollaboration({
        title: createForm.title,
        description: createForm.description,
        category: createForm.category,
        location: createForm.location || undefined,
        budget: createForm.budget ? parseFloat(createForm.budget) : undefined,
        deadline: createForm.deadline || undefined,
        requirements: createForm.requirements.split(',').map(req => req.trim()).filter(req => req),
      });

      // Update local state immediately
      setMyCollaborations(prev => [newCollaboration, ...prev]);
      setCollaborations(prev => [newCollaboration, ...prev]);
      
      setShowCreateModal(false);
      setCreateError('');
      setCreateForm({
        title: '',
        description: '',
        category: '' as ArtCategory,
        location: '',
        budget: '',
        deadline: '',
        requirements: '',
      });
    } catch (err) {
      if (err instanceof ContentModerationError) {
        const key = err.categories.includes('sexual')
          ? 'moderation.block.sexual'
          : err.categories.includes('profanity')
            ? 'moderation.block.profanity'
            : 'moderation.block.generic';
        setCreateError(t(key, 'Your content violates our community guidelines.'));
      } else {
        console.error('Error creating collaboration:', err);
        setCreateError(select({ es: 'Error al crear la colaboración.', en: 'Error creating collaboration.' }));
      }
    }
  };

  const handleApplyToCollaboration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCollaboration) return;

    setApplicationError('');
    try {
      const newApplication = await collaborationsService.applyToCollaboration(selectedCollaboration.id, applicationMessage);
      
      // Update local state immediately
      setMyApplications(prev => [...prev, {
        ...newApplication,
        collaborations: selectedCollaboration
      }]);
      
      // Clear form and close modal
      setShowApplicationModal(false);
      setApplicationMessage('');
      setSelectedCollaboration(null);
      setApplicationError('');
    } catch (err) {
      if (err instanceof ContentModerationError) {
        const key = err.categories.includes('sexual')
          ? 'moderation.block.sexual'
          : err.categories.includes('profanity')
            ? 'moderation.block.profanity'
            : 'moderation.block.generic';
        setApplicationError(t(key, 'Your content violates our community guidelines.'));
      } else {
        console.error('Error applying to collaboration:', err);
        setApplicationError(select({ es: 'Error al enviar la aplicación.', en: 'Error submitting application.' }));
      }
    }
  };

  const handleViewDetails = (collaboration: Record<string, unknown>) => {
    setSelectedCollaboration(collaboration);
    setShowDetailModal(true);
  };

  const handleApplyFromDetail = async (collaborationId: string, message: string) => {
    try {
      const newApplication = await collaborationsService.applyToCollaboration(collaborationId, message);
      
      // Update local state immediately
      setMyApplications(prev => [...prev, {
        ...newApplication,
        collaborations: selectedCollaboration
      }]);
      
      // Update collaborations list to show new application count
      setCollaborations(prev => prev.map(collab => 
        collab.id === collaborationId 
          ? {
              ...collab,
              collaboration_applications: [
                ...(collab.collaboration_applications || []),
                newApplication
              ]
            }
          : collab
      ));
    } catch (err) {
      if (err instanceof ContentModerationError) {
        const key = err.categories.includes('sexual')
          ? 'moderation.block.sexual'
          : err.categories.includes('profanity')
            ? 'moderation.block.profanity'
            : 'moderation.block.generic';
        setApplicationError(t(key, 'Your content violates our community guidelines.'));
      } else {
        console.error('Error applying to collaboration:', err);
      }
      throw err;
    }
  };

  const handleUpdateApplicationStatus = async (applicationId: string, status: 'accepted' | 'rejected') => {
    try {
      // Update application status in database
      const { error } = await supabase
        .from('collaboration_applications')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', applicationId);

      if (error) throw error;

      // Update local state immediately
      setMyCollaborations(prev => prev.map(collab => ({
        ...collab,
        collaboration_applications: Array.isArray(collab.collaboration_applications)
          ? (collab.collaboration_applications as Record<string, unknown>[]).map((app) =>
              (app.id as string) === applicationId ? { ...app, status } : app
            )
          : collab.collaboration_applications
      })));

      // Also update the collaborations list if viewing
      setCollaborations(prev => prev.map(collab => ({
        ...collab,
        collaboration_applications: Array.isArray(collab.collaboration_applications)
          ? (collab.collaboration_applications as Record<string, unknown>[]).map((app) =>
              (app.id as string) === applicationId ? { ...app, status } : app
            )
          : collab.collaboration_applications
      })));
    } catch (err) {
      console.error('Error updating application status:', err);
      alert(select({ es: 'Error al actualizar el estado de la aplicación', en: 'Error updating the application status' }));
    }
  };

  const handleUpdateCollaboration = (updatedCollaboration: Record<string, unknown>) => {
    // Update local state for collaborations
    setCollaborations(prev => prev.map(collab => 
      collab.id === updatedCollaboration.id ? updatedCollaboration : collab
    ));
    
    // Update local state for myCollaborations
    setMyCollaborations(prev => prev.map(collab => 
      collab.id === updatedCollaboration.id ? updatedCollaboration : collab
    ));

    // Update selectedCollaboration if it's the one being edited
    if (selectedCollaboration?.id === updatedCollaboration.id) {
      setSelectedCollaboration(updatedCollaboration);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString(locale);

  const renderCollaborations = (collaborationsList: Record<string, unknown>[]) => (
    <div className="space-y-6">
      {collaborationsList.map((collaboration) => (
        <div key={collaboration.id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-left">{collaboration.title}</h3>
              
              <div className="flex items-start justify-start gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[collaboration.status as keyof typeof statusColors]}`}>
                  {statusLabels[collaboration.status as keyof typeof statusLabels]}
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                  {categoryLabels[collaboration.category as ArtCategory]}
                </span>
              </div>
              
              <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                <div className="flex items-center gap-1">
                  <img
                    src={collaboration.profiles?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=24&h=24&fit=crop'}
                    alt={collaboration.profiles?.full_name}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <span 
                    className="cursor-pointer hover:text-purple-600 hover:underline transition-colors font-medium"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Navigation data:', {
                        authorId: collaboration.author_id,
                        profilesId: collaboration.profiles?.id,
                        currentUserId: user?.id
                      });
                      const event = new CustomEvent('navigate', {
                        detail: {
                          tab: 'profile',
                          userId: collaboration.author_id
                        }
                      });
                      window.dispatchEvent(event);
                    }}
                  >
                    {collaboration.profiles?.full_name}
                  </span>
                  {collaboration.profiles?.is_premium && <span className="text-yellow-500">✨</span>}
                </div>
                
                {collaboration.location && (
                  <div className="flex items-center gap-1">
                    <MapPin size={14} />
                    <span>{collaboration.location}</span>
                  </div>
                )}
                
                {collaboration.budget && (
                  <div className="flex items-center gap-1">
                    <DollarSign size={14} />
                    <span>{formatPrice(collaboration.budget)}</span>
                  </div>
                )}
                
                {collaboration.deadline && (
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    <span>
                      {select({ es: 'Fecha fin:', en: 'Deadline:' })} {formatDate(collaboration.deadline)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <p className="text-gray-700 mb-4 line-clamp-3">{collaboration.description}</p>

          {collaboration.requirements && collaboration.requirements.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-900 mb-2">{select({ es: 'Requisitos:', en: 'Requirements:' })}</h4>
              <div className="flex flex-wrap gap-2">
                {collaboration.requirements.map((req: string, index: number) => (
                  <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                    {req}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Users size={14} />
                <span>
                  {collaboration.collaboration_applications?.length || 0}{' '}
                  {select({ es: 'aplicaciones', en: 'applications' })}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Clock size={14} />
                <span>
                  {select({ es: 'Creada el:', en: 'Created on:' })} {formatDate(collaboration.created_at)}
                </span>
              </div>
            </div>
            
            {/* Buttons moved below */}
          </div>

          {/* Action Buttons - Now below and inline */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
            <div className="flex gap-2 w-full">
              <button
                onClick={() => handleViewDetails(collaboration)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                <Eye size={16} />
                <span className="hidden sm:inline">{select({ es: 'Ver detalles', en: 'View details' })}</span>
                <span className="sm:hidden">{select({ es: 'Detalles', en: 'Details' })}</span>
              </button>

              {activeTab === 'browse' && collaboration.status === 'open' && collaboration.profiles?.id !== user?.id && (
                <button
                  onClick={() => {
                    setSelectedCollaboration(collaboration);
                    setApplicationError('');
                    setApplicationMessage('');
                    setShowApplicationModal(true);
                  }}
                  className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                >
                  {select({ es: 'Aplicar', en: 'Apply' })}
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      {collaborationsList.length === 0 && (
        <div className="text-center py-12">
          <Briefcase size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {activeTab === 'browse'
              ? select({ es: 'No hay colaboraciones disponibles', en: 'No collaborations available' })
              : select({ es: 'No tienes colaboraciones', en: 'You have no collaborations' })}
          </h3>
          <p className="text-gray-500">
            {activeTab === 'browse' 
              ? select({ es: 'Intenta con otros filtros de búsqueda', en: 'Try different search filters' })
              : select({ es: 'Crea tu primera colaboración para empezar', en: 'Create your first collaboration to get started' })
            }
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Briefcase className="text-purple-600" size={24} />
          {select({ es: 'Colaboraciones', en: 'Collaborations' })}
        </h1>
        <button
          onClick={() => {
            setCreateError('');
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus size={16} />
          {select({ es: 'Añadir', en: 'Add' })}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('browse')}
            className={`py-2 px-1 border-b-2 transition-colors ${
              activeTab === 'browse'
                ? 'border-purple-500 text-purple-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {select({ es: 'Explorar', en: 'Browse' })}
          </button>
          <button
            onClick={() => setActiveTab('my-collaborations')}
            className={`py-2 px-1 border-b-2 transition-colors ${
              activeTab === 'my-collaborations'
                ? 'border-purple-500 text-purple-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {select({ es: 'Publicadas por mi', en: 'Published by me' })}
          </button>
          <button
            onClick={() => setActiveTab('my-applications')}
            className={`py-2 px-1 border-b-2 transition-colors ${
              activeTab === 'my-applications'
                ? 'border-purple-500 text-purple-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {select({ es: 'Mis solicitudes', en: 'My applications' })}
          </button>
        </nav>
      </div>

      {/* Filters (only for browse tab) */}
      {activeTab === 'browse' && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{select({ es: 'Categoría', en: 'Category' })}</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters(prev => ({...prev, category: e.target.value as ArtCategory | 'all'}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">{select({ es: 'Todas las categorías', en: 'All categories' })}</option>
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{select({ es: 'Estado', en: 'Status' })}</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({...prev, status: e.target.value as typeof filters.status}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">{select({ es: 'Todos los estados', en: 'All statuses' })}</option>
                <option value="open">{statusLabels.open}</option>
                <option value="in_progress">{statusLabels.in_progress}</option>
                <option value="completed">{statusLabels.completed}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{select({ es: 'Ubicación', en: 'Location' })}</label>
              <input
                type="text"
                value={filters.location}
                onChange={(e) => setFilters(prev => ({...prev, location: e.target.value}))}
                placeholder={select({ es: 'Ciudad o país...', en: 'City or country...' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-200 h-48 rounded-xl"></div>
          ))}
        </div>
      ) : (
        <>
          {activeTab === 'browse' && renderCollaborations(collaborations)}
          {activeTab === 'my-collaborations' && renderCollaborations(myCollaborations)}
          {activeTab === 'my-applications' && (
            <div className="space-y-6">
              {myApplications.map((application) => (
                <div key={application.id} className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {application.collaborations.title}
                      </h3>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 font-medium">{select({ es: 'Estado:', en: 'Status:' })}</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${applicationStatusColors[application.status as keyof typeof applicationStatusColors]}`}>
                    {applicationStatusLabels[application.status as keyof typeof applicationStatusLabels]}
                  </span>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[application.collaborations.status as keyof typeof statusColors]}`}>
                  {select({ es: 'Proyecto:', en: 'Project:' })} {statusLabels[application.collaborations.status as keyof typeof statusLabels]}
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                  {categoryLabels[application.collaborations.category as ArtCategory]}
                </span>
              </div>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-4">{application.collaborations.description}</p>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">{select({ es: 'Tu mensaje de aplicación:', en: 'Your application message:' })}</h4>
                    <p className="text-gray-700">{application.message}</p>
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>
                        {select({ es: 'Aplicado el', en: 'Applied on' })} {formatDate(application.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>{select({ es: 'Por:', en: 'By:' })}</span>
                      <span 
                        className="cursor-pointer hover:text-purple-600 hover:underline transition-colors font-medium"
                        onClick={() => {
                         console.log('Navigation data:', {
                           authorId: application.collaborations.author_id,
                           profilesId: application.collaborations.profiles?.id,
                           currentUserId: user?.id
                         });
                          const event = new CustomEvent('navigate', {
                            detail: {
                              tab: 'profile',
                             userId: application.collaborations.author_id
                            }
                          });
                          window.dispatchEvent(event);
                        }}
                      >
                        {application.collaborations.profiles?.full_name || select({ es: 'Usuario', en: 'User' })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Collaboration Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{select({ es: 'Nueva colaboración', en: 'New collaboration' })}</h2>
                <button
                  onClick={() => {
                    setCreateError('');
                    setShowCreateModal(false);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateCollaboration} className="space-y-6">
                {createError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {createError}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{select({ es: 'Título', en: 'Title' })}</label>
                  <input
                    type="text"
                    required
                    value={createForm.title}
                    onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={select({ es: 'Busco ilustrador para proyecto musical...', en: 'Looking for an illustrator for a music project...' })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{select({ es: 'Categoría', en: 'Category' })}</label>
                  <select
                    required
                    value={createForm.category}
                    onChange={(e) => setCreateForm({...createForm, category: e.target.value as ArtCategory})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">{select({ es: 'Selecciona una categoría', en: 'Select a category' })}</option>
                    {Object.entries(categoryLabels).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{select({ es: 'Descripción', en: 'Description' })}</label>
                  <textarea
                    required
                    rows={4}
                    value={createForm.description}
                    onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    placeholder={select({ es: 'Describe el proyecto, lo que necesitas, objetivos...', en: 'Describe the project, what you need, goals...' })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{select({ es: 'Ubicación (opcional)', en: 'Location (optional)' })}</label>
                    <input
                      type="text"
                      value={createForm.location}
                      onChange={(e) => setCreateForm({...createForm, location: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder={select({ es: 'Madrid, España', en: 'Madrid, Spain' })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{select({ es: 'Presupuesto (opcional)', en: 'Budget (optional)' })}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={createForm.budget}
                      onChange={(e) => setCreateForm({...createForm, budget: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{select({ es: 'Fecha límite (opcional)', en: 'Deadline (optional)' })}</label>
                  <input
                    type="date"
                    value={createForm.deadline}
                    onChange={(e) => setCreateForm({...createForm, deadline: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {select({ es: 'Requisitos (separados por comas)', en: 'Requirements (comma separated)' })}
                  </label>
                  <input
                    type="text"
                    value={createForm.requirements}
                    onChange={(e) => setCreateForm({...createForm, requirements: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={select({ es: 'Experiencia en diseño, portfolio disponible, disponibilidad inmediata', en: 'Design experience, portfolio available, immediate availability' })}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {select({ es: 'Cancelar', en: 'Cancel' })}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {select({ es: 'Crear colaboración', en: 'Create collaboration' })}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Application Modal */}
      {showApplicationModal && selectedCollaboration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {select({ es: 'Aplicar a:', en: 'Apply to:' })} {selectedCollaboration.title}
            </h2>

            <form onSubmit={handleApplyToCollaboration} className="space-y-4">
              {applicationError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {applicationError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {select({ es: 'Mensaje de aplicación', en: 'Application message' })}
                </label>
                <textarea
                  required
                  rows={4}
                  value={applicationMessage}
                  onChange={(e) => setApplicationMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  placeholder={select({ es: 'Explica por qué eres el candidato ideal para esta colaboración...', en: 'Explain why you are the ideal candidate for this collaboration...' })}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowApplicationModal(false);
                    setSelectedCollaboration(null);
                    setApplicationMessage('');
                    setApplicationError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {select({ es: 'Cancelar', en: 'Cancel' })}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  {select({ es: 'Enviar aplicación', en: 'Send application' })}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Collaboration Detail Modal */}
      {showDetailModal && selectedCollaboration && (
        <CollaborationDetailModal
          collaboration={selectedCollaboration}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedCollaboration(null);
          }}
          onApply={handleApplyFromDetail}
          onUpdateApplication={handleUpdateApplicationStatus}
          onUpdate={handleUpdateCollaboration}
        />
      )}
    </div>
  );
};

export default CollaborationsView;
